import React from 'react';

import useAuth from 'src/hooks/useAuth';
import {useUpdateUserSettingsMutation} from 'src/hooks/useUserProfile';
import {ApiOrganization} from 'src/lib/api/auth';
import {Translator, translate} from 'src/lib/translate';
import {Locale} from 'src/lib/translations';

import {recordEvent} from './Analytics';

export type {Translator} from 'src/lib/translate';
export {translate} from 'src/lib/translate';

type LocaleContextType = {
  locale: Locale;
  changeLocale: (newLocale: Locale) => void;
  t: Translator;
};

const TranslationsContext = React.createContext<LocaleContextType | undefined>(undefined);

export const getLocale = (maybeOrganization: ApiOrganization | null): Locale => {
  let locale = localStorage.getItem('userLocale') as Locale | null;
  if (!locale && maybeOrganization) {
    locale = maybeOrganization.settings.locale || 'en';
  }
  return locale || 'en';
};

export function TranslationsProvider({children}: {children: React.ReactNode}) {
  const auth = useAuth();
  const updateUserSettings = useUpdateUserSettingsMutation();
  const [locale, setLocale] = React.useState<Locale>(() =>
    getLocale(auth.status === 'logged in' ? auth.currentOrganization : null)
  );

  React.useEffect(() => {
    if (auth.status === 'logged in' && auth.currentOrganization) {
      // Use user settings if available, otherwise fall back to organization settings
      if (auth.userProfile?.settings?.locale) {
        setLocale(auth.userProfile.settings.locale);
      } else {
        setLocale(getLocale(auth.currentOrganization));
      }
    }
  }, [auth]);

  const changeLocale = (newLocale: Locale): void => {
    if (auth.status === 'logged in') {
      setLocale(newLocale);
      recordEvent('change_locale', {locale: newLocale});

      // The effect above re-applies the cached userProfile locale whenever
      // auth rebuilds (e.g. hourly token refresh), so this write has to land
      // in the cache, not just the API.
      updateUserSettings.mutate(
        {locale: newLocale},
        {
          onError: (error) => {
            console.error('Failed to update user locale setting:', error);
          },
        }
      );
    }
  };

  const t: Translator = (key, variables = {}) => translate(locale, key, variables);

  const value = {
    locale,
    changeLocale,
    t,
  };

  return <TranslationsContext.Provider value={value}>{children}</TranslationsContext.Provider>;
}

// Custom hook to use translations
export function useTranslations() {
  const context = React.useContext(TranslationsContext);
  if (context === undefined) {
    throw new Error('useTranslations must be used within a TranslationsProvider');
  }
  return context;
}
